/**
 * "Repairable" (AD-672 S2, `q-shard-sink-repair-verb`) — the pure half of the NPC
 * panel's repair row. From the player's own inventory snapshot it lists every
 * instance a vendor could repair: durability-bearing, below max, in the bag or
 * worn. Most worn first (lowest current/max), then by name.
 *
 * It decides NOTHING the server does not re-check: no price is shown or computed
 * here (the rate is server config, a [P5.6] seed), and ownership, location,
 * damage and the whole-price rule are all enforced by the commit. Pure;
 * node-testable.
 */

/** The `inventorySnapshot.items` fields this reads. */
export interface RepairItemView {
  id: string;
  name: string;
  location: string;
  durabilityCurrent: number | null;
  durabilityMax: number | null;
}

export interface RepairableItem {
  id: string;
  name: string;
  durabilityCurrent: number;
  durabilityMax: number;
  worn: boolean;
}

export function repairableItems(items: readonly RepairItemView[]): RepairableItem[] {
  const out: RepairableItem[] = [];
  for (const it of items) {
    if (it.durabilityCurrent === null || it.durabilityMax === null) continue;
    if (it.durabilityCurrent >= it.durabilityMax) continue;
    if (it.location !== 'inventory' && it.location !== 'equipped') continue;
    out.push({
      id: it.id,
      name: it.name,
      durabilityCurrent: it.durabilityCurrent,
      durabilityMax: it.durabilityMax,
      worn: it.location === 'equipped',
    });
  }
  return out.sort(
    (a, b) =>
      a.durabilityCurrent / a.durabilityMax - b.durabilityCurrent / b.durabilityMax ||
      a.name.localeCompare(b.name),
  );
}

/** The toast line for a `repairResult` refusal (the typed reason, in words). */
export function describeRepairDenial(denied: string): string {
  switch (denied) {
    case 'insufficientShards':
      return 'Not enough Shards for this repair.';
    case 'notDamaged':
      return 'Already in full repair.';
    case 'notRepairable':
      return 'That item cannot be repaired.';
    case 'itemNotFound':
      return 'That item is not in your bag or worn.';
    case 'stale':
      return 'It changed while you asked — try again.';
    case 'reputationLocked':
      return 'This smith will not serve you yet.';
    default:
      return 'This NPC does not repair.';
  }
}
